import { BudgetProposal, SyncResult, SyncLog, SyncStats, BudgetMilestone, ProjectTask } from '../types/budgetSync';
import { supabase } from './supabaseService';
import { notificationService } from './notificationService';
import { structuredLogger } from './structuredLogger';
import { logger } from '../utils/logger';

const SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutos
let syncIntervalId: number | null = null;

interface BudgetSyncConfig {
  api_url: string;
  api_key: string;
  source: 'env' | 'database';
}

/**
 * BudgetSyncService — Sincronización con el sistema externo de presupuestos.
 *
 * Las edge functions `get-pending-proposals` y `mark-as-synced` NO viven en este
 * repo (CRM). Residen en el proyecto Supabase externo apuntado por VITE_BOLT_DATABASE_URL.
 * Este servicio es un consumidor cross-project: lee propuestas aceptadas del sistema
 * externo y crea proyectos/contactos en el CRM local.
 *
 * Si no hay configuración (env vars o DB config), el servicio se desactiva silenciosamente.
 */
class BudgetSyncService {
  private runtimeConfig: BudgetSyncConfig | null = null;

  constructor() {
    // Constructor simplificado - la configuración se verifica dinámicamente
    const isConfigured = this.isConfigured();
    if (isConfigured) {
      const url = import.meta.env.VITE_BOLT_DATABASE_URL || '';
      structuredLogger.info('BudgetSyncService initialized', {
        url: url.substring(0, 30) + '...'
      });
    } else {
      structuredLogger.warn('BudgetSyncService not configured', {
        hasUrl: !!import.meta.env.VITE_BOLT_DATABASE_URL,
        hasKey: !!import.meta.env.VITE_BOLT_DATABASE_SERVICE_ROLE_KEY
      });
    }
  }

  /**
   * Obtiene la configuración activa desde la base de datos
   */
  async getConfigFromDatabase(): Promise<BudgetSyncConfig | null> {
    try {
      const { data, error } = await supabase
        .rpc('get_active_budget_sync_config');

      if (error) {
        // Silently handle if function doesn't exist (not critical)
        if (error.code === '42883' || error.message?.includes('does not exist')) {
          logger.info('Budget sync RPC function not available (not critical)');
        } else {
          structuredLogger.warn('Could not fetch budget sync config from database', { error });
        }
        return null;
      }

      if (data && data.length > 0) {
        const config = data[0];
        return {
          api_url: config.api_url,
          api_key: config.api_key,
          source: 'database'
        };
      }

      return null;
    } catch (error) {
      structuredLogger.error('Error fetching budget sync config from database', { error });
      return null;
    }
  }

  /**
   * Obtiene la configuración desde variables de entorno o base de datos
   */
  async getActiveConfig(): Promise<BudgetSyncConfig | null> {
    // Primero intentar con variables de entorno
    const url = import.meta.env.VITE_BOLT_DATABASE_URL;
    const key = import.meta.env.VITE_BOLT_DATABASE_SERVICE_ROLE_KEY;

    if (url && key && url.trim() !== '' && key.trim() !== '') {
      return {
        api_url: url,
        api_key: key,
        source: 'env'
      };
    }

    // Si no hay variables de entorno, intentar con base de datos
    if (this.runtimeConfig) {
      return this.runtimeConfig;
    }

    const dbConfig = await this.getConfigFromDatabase();
    if (dbConfig) {
      this.runtimeConfig = dbConfig;
      return dbConfig;
    }

    return null;
  }

  /**
   * Verifica dinámicamente si el servicio está configurado
   * Prioriza configuración de base de datos sobre variables de entorno
   */
  isConfigured(): boolean {
    // First check if we have runtime config from database
    if (this.runtimeConfig) {
      const isConfigured = !!(this.runtimeConfig.api_url && this.runtimeConfig.api_key &&
                              this.runtimeConfig.api_url.trim() !== '' &&
                              this.runtimeConfig.api_key.trim() !== '');

      if (isConfigured) {
        logger.info('Budget sync configurado desde base de datos');
        return true;
      }
    }

    // If no runtime config, try to load from database (async, will update for next check)
    if (!this.runtimeConfig) {
      logger.debug('No runtime config, cargando desde BD...');
      this.getConfigFromDatabase().then((config) => {
        if (config) {
          this.runtimeConfig = config;
          logger.info('Budget sync config cargada desde BD');
        }
      }).catch(console.error);
    }

    // Fallback to environment variables
    const url = import.meta.env.VITE_BOLT_DATABASE_URL;
    const key = import.meta.env.VITE_BOLT_DATABASE_SERVICE_ROLE_KEY;

    const isConfigured = !!(url && key && url.trim() !== '' && key.trim() !== '');

    // Solo logear si no está configurado
    if (!isConfigured) {
      console.log('⚠️ Budget sync configuration check:', {
        hasUrl: !!url,
        hasKey: !!key,
        hasRuntimeConfig: !!this.runtimeConfig,
        note: 'No configuration found in env or database'
      });
    }

    return isConfigured;
  }

  /**
   * Verifica si hay alguna configuración disponible (env o database)
   */
  async isConfiguredAsync(): Promise<boolean> {
    const config = await this.getActiveConfig();
    return config !== null;
  }

  /**
   * Obtiene detalles de la configuración para mostrar en la UI
   */
  getConfigurationDetails(): {
    isConfigured: boolean;
    hasUrl: boolean;
    hasKey: boolean;
    urlPreview?: string;
    needsRestart: boolean;
  } {
    const url = import.meta.env.VITE_BOLT_DATABASE_URL;
    const key = import.meta.env.VITE_BOLT_DATABASE_SERVICE_ROLE_KEY;

    const hasUrl = !!(url && url.trim() !== '');
    const hasKey = !!(key && key.trim() !== '');
    const isConfigured = hasUrl && hasKey;

    return {
      isConfigured,
      hasUrl,
      hasKey,
      urlPreview: hasUrl ? url.substring(0, 40) + '...' : undefined,
      needsRestart: !isConfigured
    };
  }

  /**
   * Prueba la conexión con el sistema de presupuestos
   */
  async testConnection(): Promise<{
    success: boolean;
    message: string;
    details?: any;
  }> {
    const config = await this.getActiveConfig();
    if (!config) {
      return {
        success: false,
        message: 'No hay configuración disponible. Las variables de entorno no están configuradas y no se encontró configuración en la base de datos. Agrega las variables al archivo .env y reinicia el servidor, o configura las credenciales a través del panel de administración.'
      };
    }

    const budgetSystemUrl = config.api_url;
    const serviceRoleKey = config.api_key;

    try {
      structuredLogger.info('Testing budget system connection');

      const response = await fetch(
        `${budgetSystemUrl}/functions/v1/get-pending-proposals`,
        {
          headers: {
            'Authorization': `Bearer ${serviceRoleKey}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          message: `Error de conexión: HTTP ${response.status}`,
          details: errorText
        };
      }

      const result = await response.json();

      return {
        success: true,
        message: `Conexión exitosa. Se encontraron ${result.count || 0} propuestas pendientes.`,
        details: {
          totalProposals: result.count || 0,
          url: budgetSystemUrl.substring(0, 40) + '...',
          configSource: config.source
        }
      };
    } catch (error: any) {
      structuredLogger.error('Connection test failed', { error });
      return {
        success: false,
        message: `Error al conectar: ${error.message}`,
        details: error
      };
    }
  }

  async getPendingProposals(): Promise<{ proposals: BudgetProposal[]; count: number }> {
    const config = await this.getActiveConfig();
    if (!config) {
      throw new Error('Budget sync service not configured. Check environment variables or database configuration.');
    }

    const budgetSystemUrl = config.api_url;
    const serviceRoleKey = config.api_key;

    structuredLogger.info('Fetching pending proposals', { source: config.source });

    try {
      const response = await fetch(
        `${budgetSystemUrl}/functions/v1/get-pending-proposals`,
        {
          headers: {
            'Authorization': `Bearer ${serviceRoleKey}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();

      // Filtrar solo propuestas con estado 'accepted'
      const acceptedProposals = (result.data || []).filter((proposal: BudgetProposal) => {
        const isAccepted = proposal.status === 'accepted';
        if (!isAccepted) {
          console.log('⏭️ Proposal filtered out - not accepted:', {
            proposal_id: proposal.proposal_id,
            status: proposal.status
          });
        }
        return isAccepted;
      });

      structuredLogger.info('Fetched pending proposals', {
        total: result.count || 0,
        accepted: acceptedProposals.length,
        filtered: (result.count || 0) - acceptedProposals.length
      });

      return {
        proposals: acceptedProposals,
        count: acceptedProposals.length
      };
    } catch (error) {
      structuredLogger.error('Error fetching pending proposals', { error });
      throw error;
    }
  }

  async markAsSynced(data: {
    proposal_id: string;
    crm_record_id: string;
    budget_id?: string;
    duration_ms?: number;
    response_data?: any;
  }): Promise<void> {
    const config = await this.getActiveConfig();
    if (!config) {
      throw new Error('Budget sync service not configured');
    }

    const budgetSystemUrl = config.api_url;
    const serviceRoleKey = config.api_key;

    try {
      const response = await fetch(
        `${budgetSystemUrl}/functions/v1/mark-as-synced`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${serviceRoleKey}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to mark as synced: ${errorText}`);
      }

      structuredLogger.info('Marked proposal as synced', {
        proposal_id: data.proposal_id,
        crm_record_id: data.crm_record_id
      });

      return await response.json();
    } catch (error) {
      structuredLogger.error('Error marking proposal as synced', { error, data });
      throw error;
    }
  }

  private async createOrUpdateClient(clientData: {
    name: string;
    email: string;
    company?: string;
    phone?: string;
  }): Promise<string> {
    // Use the database function for safe UPSERT to prevent duplicates
    const { data: result, error: upsertError } = await supabase
      .rpc('upsert_contact', {
        p_full_name: clientData.name,
        p_email: clientData.email.toLowerCase(),
        p_phone: clientData.phone || '',
        p_company: clientData.company || clientData.name,
        p_role: 'Cliente',
        p_lead_status: 'ya_es_cliente',
        p_product_interest: 'Proyecto desde presupuesto',
        p_owner: 'Sistema Presupuestos',
        p_notes: `Cliente desde presupuesto sincronizado el ${new Date().toLocaleDateString()}`
      });

    if (upsertError) {
      structuredLogger.error('Error upserting contact', { error: upsertError });
      throw upsertError;
    }

    if (!result) {
      throw new Error('upsert_contact did not return a contact ID');
    }

    structuredLogger.info('Contact upserted successfully', { contact_id: result });
    return result;
  }

  private async createProject(projectData: {
    client_id: string;
    client_name: string;
    name: string;
    description?: string;
    budget: number;
    total_hours: number;
    hourly_rate: number;
    start_date?: string;
    duration_months: number;
    tech_stack: string[];
    milestones: BudgetMilestone[];
    external_id: string;
    budget_id?: string;
    client_email?: string;
    client_company?: string;
    client_phone?: string;
  }): Promise<string> {
    const { data: existingProject } = await supabase
      .from('projects')
      .select('id')
      .eq('external_id', projectData.external_id)
      .maybeSingle();

    if (existingProject) {
      structuredLogger.warn('Project already exists', { project_id: existingProject.id });
      return existingProject.id;
    }

    const endDate = projectData.start_date
      ? new Date(projectData.start_date)
      : new Date();
    endDate.setMonth(endDate.getMonth() + projectData.duration_months);

    const { data: newProject, error: projectError } = await supabase
      .from('projects')
      .insert({
        name: projectData.name,
        client_id: projectData.client_id,
        client_name: projectData.client_name,
        description: projectData.description,
        status: 'active',
        budget: projectData.budget,
        total_hours: projectData.total_hours,
        hourly_rate: projectData.hourly_rate,
        start_date: projectData.start_date || new Date().toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        duration_months: projectData.duration_months,
        tech_stack: projectData.tech_stack,
        milestones: projectData.milestones,
        external_id: projectData.external_id,
        budget_id: projectData.budget_id,
        kanban_status: 'planning',
        // Guardar datos de respaldo del cliente
        client_email_backup: projectData.client_email,
        client_company_backup: projectData.client_company,
        client_phone_backup: projectData.client_phone
      })
      .select()
      .single();

    if (projectError) {
      structuredLogger.error('Error creating project', { error: projectError });
      throw projectError;
    }

    structuredLogger.info('Created new project', { project_id: newProject.id });
    return newProject.id;
  }

  private async generateTasksFromMilestones(
    projectId: string,
    milestones: BudgetMilestone[]
  ): Promise<void> {
    const tasks: any[] = [];
    let orderIndex = 0;

    milestones.forEach((milestone, milestoneIndex) => {
      milestone.tasks.forEach((task, taskIndex) => {
        tasks.push({
          project_id: projectId,
          title: task.description,
          description: `Milestone: ${milestone.title}\nHoras estimadas: ${task.hours}h`,
          status: 'backlog',
          priority: milestoneIndex === 0 ? 'high' : milestoneIndex === 1 ? 'medium' : 'low',
          milestone_id: milestone.title,
          estimated_hours: task.hours,
          order_index: orderIndex++
        });
      });
    });

    if (tasks.length > 0) {
      const { error } = await supabase
        .from('project_tasks')
        .insert(tasks);

      if (error) {
        structuredLogger.error('Error creating tasks', { error });
        throw error;
      }

      structuredLogger.info('Generated tasks from milestones', {
        project_id: projectId,
        task_count: tasks.length
      });
    }
  }

  private async logSync(logData: {
    proposal_id: string;
    sync_status: 'pending' | 'success' | 'error';
    error_message?: string;
    crm_project_id?: string;
    payload: any;
    duration_ms: number;
  }): Promise<void> {
    const { error } = await supabase
      .from('budget_sync_logs')
      .insert({
        proposal_id: logData.proposal_id,
        sync_status: logData.sync_status,
        error_message: logData.error_message,
        crm_project_id: logData.crm_project_id,
        payload: logData.payload,
        duration_ms: logData.duration_ms,
        retry_count: 0
      });

    if (error) {
      structuredLogger.error('Error logging sync', { error });
    }
  }

  async syncProposals(): Promise<SyncResult> {
    if (!this.isConfigured()) {
      structuredLogger.warn('Sync skipped: service not configured');
      return { success: false, synced: 0, errors: 1, total: 0 };
    }

    const startTime = Date.now();

    try {
      structuredLogger.info('Starting proposal sync');

      const { proposals, count } = await this.getPendingProposals();

      if (count === 0) {
        structuredLogger.info('No pending proposals to sync');
        return { success: true, synced: 0, errors: 0, total: 0 };
      }

      let synced = 0;
      let errors = 0;
      const errorDetails: Array<{ proposal_id: string; error: string }> = [];

      for (const proposal of proposals) {
        const proposalStartTime = Date.now();

        // Validación adicional de seguridad: verificar estado
        if (proposal.status !== 'accepted') {
          structuredLogger.warn('Skipping non-accepted proposal', {
            proposal_id: proposal.proposal_id,
            status: proposal.status
          });
          continue;
        }

        try {
          const clientId = await this.createOrUpdateClient({
            name: proposal.client.name,
            email: proposal.client.email,
            company: proposal.client.company,
            phone: proposal.client.phone
          });

          const projectId = await this.createProject({
            client_id: clientId,
            client_name: proposal.client.name,
            name: proposal.project.title,
            description: proposal.project.description,
            budget: proposal.project.total_cost,
            total_hours: proposal.project.total_hours,
            hourly_rate: proposal.project.hourly_rate,
            start_date: proposal.project.date,
            duration_months: proposal.project.duration,
            tech_stack: proposal.project.techStack || [],
            milestones: proposal.milestones,
            external_id: proposal.proposal_id,
            budget_id: proposal.budget_id,
            // Pasar datos del cliente para backup
            client_email: proposal.client.email,
            client_company: proposal.client.company,
            client_phone: proposal.client.phone
          });

          await this.generateTasksFromMilestones(projectId, proposal.milestones);

          await this.markAsSynced({
            proposal_id: proposal.proposal_id,
            crm_record_id: projectId,
            budget_id: proposal.budget_id,
            duration_ms: Date.now() - proposalStartTime,
            response_data: { status: 'created' }
          });

          await this.logSync({
            proposal_id: proposal.proposal_id,
            sync_status: 'success',
            crm_project_id: projectId,
            payload: proposal,
            duration_ms: Date.now() - proposalStartTime
          });

          await notificationService.createNotification({
            code: 'PROJECT.SYNCED_FROM_BUDGET',
            title: 'Nuevo Proyecto Sincronizado',
            body: `Se ha sincronizado el proyecto "${proposal.project.title}" desde el presupuestador`,
            severity: 'info',
            data: {
              project_id: projectId,
              project_name: proposal.project.title,
              client_name: proposal.client.name,
              budget: proposal.project.total_cost
            }
          });

          synced++;
        } catch (error: any) {
          structuredLogger.error('Error syncing proposal', {
            proposal_id: proposal.proposal_id,
            error
          });

          await this.logSync({
            proposal_id: proposal.proposal_id,
            sync_status: 'error',
            error_message: error.message || 'Unknown error',
            payload: proposal,
            duration_ms: Date.now() - proposalStartTime
          });

          errors++;
          errorDetails.push({
            proposal_id: proposal.proposal_id,
            error: error.message || 'Unknown error'
          });
        }
      }

      const duration = Date.now() - startTime;
      structuredLogger.info('Sync completed', { synced, errors, total: count, duration });

      return { success: true, synced, errors, total: count, errorDetails };
    } catch (error: any) {
      structuredLogger.error('Sync failed', { error });
      return {
        success: false,
        synced: 0,
        errors: 1,
        total: 0,
        errorDetails: [{ proposal_id: 'unknown', error: error.message }]
      };
    }
  }

  startAutoSync(): void {
    if (!this.isConfigured()) {
      structuredLogger.warn('Auto-sync not started: service not configured');
      return;
    }

    if (syncIntervalId !== null) {
      structuredLogger.warn('Auto-sync already running');
      return;
    }

    structuredLogger.info('Starting auto-sync', { interval: SYNC_INTERVAL });

    this.syncProposals();

    syncIntervalId = window.setInterval(() => {
      this.syncProposals();
    }, SYNC_INTERVAL);
  }

  stopAutoSync(): void {
    if (syncIntervalId !== null) {
      clearInterval(syncIntervalId);
      syncIntervalId = null;
      structuredLogger.info('Auto-sync stopped');
    }
  }

  async getSyncStats(): Promise<SyncStats> {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const { data: logs, error } = await supabase
        .from('budget_sync_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      const totalPending = logs?.filter(l => l.sync_status === 'pending').length || 0;
      const syncedToday = logs?.filter(l => {
        const logDate = new Date(l.synced_at);
        return l.sync_status === 'success' && logDate >= today;
      }).length || 0;
      const totalErrors = logs?.filter(l => l.sync_status === 'error').length || 0;

      const lastSuccess = logs?.find(l => l.sync_status === 'success');
      const lastSyncAt = lastSuccess ? new Date(lastSuccess.synced_at) : undefined;

      const totalAttempts = logs?.length || 1;
      const successCount = logs?.filter(l => l.sync_status === 'success').length || 0;
      const successRate = (successCount / totalAttempts) * 100;

      return {
        totalPending,
        syncedToday,
        totalErrors,
        lastSyncAt,
        successRate
      };
    } catch (error) {
      structuredLogger.error('Error getting sync stats', { error });
      return {
        totalPending: 0,
        syncedToday: 0,
        totalErrors: 0,
        successRate: 0
      };
    }
  }

  async getRecentLogs(limit: number = 50): Promise<SyncLog[]> {
    try {
      const { data, error } = await supabase
        .from('budget_sync_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return (data || []).map(log => ({
        ...log,
        synced_at: new Date(log.synced_at),
        created_at: new Date(log.created_at)
      }));
    } catch (error) {
      structuredLogger.error('Error getting recent logs', { error });
      return [];
    }
  }
}

export const budgetSyncService = new BudgetSyncService();
